/**
 * The ZeroGap permission matrix. This is the single source of truth: the API enforces it on every
 * route (see apps/server/src/http/authz.ts), the portal uses it only to hide controls, and
 * apps/server/test/permissions.test.ts asserts every route against it with direct requests.
 *
 * Customer roles are exactly `admin` and `viewer`. Platform super-admin is NOT a tenant role; it is a
 * separate platform grant and only obtains tenant-scoped permissions through an audited support session.
 */
export const TENANT_ROLES = ['admin', 'viewer'];
export const TENANT_PERMISSIONS = [
    // read-only (admin + viewer)
    'tenant.read',
    'devices.read',
    'policies.read',
    'jobs.read',
    'catalog.read', // file metadata only (names, sizes, dates) - never contents
    'restores.read', // restore job status/history - never contents
    'reports.read',
    'usage.read',
    'audit.read',
    'users.read',
    'alerts.read',
    'mirrors.read', // see which phone syncs to which computer folder
    'devices.pair_own', // show a QR code that connects your own phone (never someone else's)
    // any member, but bounded (see MIRROR_NOTES): deliberately not named ".manage", because every
    // ".manage" permission in this matrix is admin-only and that invariant is asserted by the tests.
    'mirrors.connect', // connect or disconnect your own phone's folder on a computer
    // admin-only
    'devices.manage', // enroll, remove, retire, quarantine, groups/tags, rename
    'devices.credentials', // rotate / revoke storage + control-plane credentials
    'devices.browse', // list a connected computer's folder names live, to choose a folder to archive
    'policies.manage', // create, edit, assign, pause
    'jobs.manage', // trigger, retry, cancel
    'restores.create', // restore files or download file contents
    'shares.create', // create, change and stop shared links: anyone with a link can download its files
    'users.manage',
    'settings.manage',
    'billing.read',
    'billing.manage',
    'retention.manage',
    'notifications.manage', // tenant-wide notification settings and webhooks
    'alerts.manage', // acknowledge/resolve alerts, pause cleanup
];
const VIEWER = [
    'tenant.read',
    'devices.read',
    'policies.read',
    'jobs.read',
    'catalog.read',
    'restores.read',
    'reports.read',
    'usage.read',
    'audit.read',
    'users.read',
    'alerts.read',
    'mirrors.read',
    'mirrors.connect',
    'devices.pair_own',
];
export const ROLE_PERMISSIONS = {
    admin: new Set(TENANT_PERMISSIONS),
    viewer: new Set(VIEWER),
};
export function roleCan(role, permission) {
    return ROLE_PERMISSIONS[role].has(permission);
}
/** Permissions that expose or move customer file *contents*. Operators need a content-scoped support session. */
export const CONTENT_PERMISSIONS = new Set(['restores.create', 'devices.browse', 'shares.create']);
/**
 * Permissions an operator never holds, even in a content-scoped support session. A shared link hands
 * customer files to people outside the organization, which only the organization itself may decide.
 */
export const NEVER_FOR_OPERATORS = new Set(['shares.create']);
export const PLATFORM_PERMISSIONS = [
    'platform.tenants.read',
    'platform.tenants.manage', // create, suspend, reactivate, delete, plans
    'platform.accounts.recover', // reset MFA / trigger password reset, unlock
    'platform.sessions.revoke',
    'platform.storage.manage', // provisioning, reconciliation, storage health
    'platform.jobs.inspect', // queues, failed jobs, retries
    'platform.maintenance.run', // GC, retention, verification, drills, catalog rebuild
    'platform.plans.manage',
    'platform.settings.manage',
    'platform.releases.manage', // agent release rollout
    'platform.audit.read',
    'platform.support.start', // open a support session in a tenant (reason + step-up)
];
/** Super-admins hold every platform permission. There is deliberately no partial platform role yet. */
export function superAdminCan(_p) {
    return true;
}
export function supportSessionCan(scope, permission) {
    if (NEVER_FOR_OPERATORS.has(permission))
        return false;
    if (CONTENT_PERMISSIONS.has(permission))
        return scope === 'content';
    return roleCan('admin', permission);
}
/** Human-readable matrix used by docs generation and the portal's "Roles" help panel. */
export const PERMISSION_DESCRIPTIONS = {
    'tenant.read': 'View tenant overview and dashboard',
    'devices.read': 'View devices, health, and inventory',
    'policies.read': 'View backup policies and assignments',
    'jobs.read': 'View backup history, job status, and logs',
    'catalog.read': 'Browse backed-up file metadata (names, sizes, dates)',
    'restores.read': 'View restore jobs and their outcomes',
    'reports.read': 'View reports',
    'usage.read': 'View storage usage',
    'audit.read': 'View tenant audit events',
    'users.read': 'View tenant members and roles',
    'alerts.read': 'View alerts',
    'devices.manage': 'Enroll, rename, group, retire, quarantine, or remove devices',
    'devices.browse': "See the names of folders on a connected computer, to choose a folder to archive",
    'devices.credentials': 'Rotate or revoke device credentials',
    'policies.manage': 'Create, edit, assign, and pause policies',
    'jobs.manage': 'Trigger, retry, or cancel backup jobs',
    'restores.create': 'Restore files or download file contents',
    'shares.create': 'Create, change, and stop shared links to backed-up files',
    'users.manage': 'Invite, remove, and change roles of tenant users',
    'settings.manage': 'Change tenant settings (MFA enforcement, defaults)',
    'billing.read': 'View subscription and invoices',
    'billing.manage': 'Change plan and billing details',
    'retention.manage': 'Change retention, holds, and cleanup pause',
    'notifications.manage': 'Configure tenant notifications and webhooks',
    'alerts.manage': 'Acknowledge and resolve alerts',
    'devices.pair_own': 'Connect your own phone by scanning a QR code',
    'mirrors.read': 'See which phones send their files to which computers',
    'mirrors.connect': "Send a phone's files to a folder on a computer, or stop doing so",
};
/**
 * Why `mirrors.connect` is available to viewers: sending your own phone's files to your own computer is the
 * point of the mobile app, and every member must be able to set it up. The blast radius is bounded
 * instead of gated:
 *  - the destination must be a computer of the same organization, and the mirror writes only inside the
 *    agent's mirror root (`<user profile>/ZeroGap/<phone name>`);
 *  - choosing an arbitrary destination folder additionally requires `restores.create` (admin);
 *  - a mirror never deletes: removals move to "Recently deleted" under the same root;
 *  - a viewer may only mirror a phone they own (`devices.owner_user_id`).
 */
export const MIRROR_NOTES = 'docs/mobile.md#mirror-to-a-computer';
//# sourceMappingURL=permissions.js.map